import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Sparkles } from 'lucide-react';
import { PageHeader } from '@/components/patterns';
import { cn } from '@/lib/utils/cn';
import { useAuthStore } from '@/features/auth/store/auth-store';
import {
  SUB_VISTAS,
  VistaExplorar,
  VistaAperturas,
  VistaSinonimos,
  VistaVersiones,
  type SubVista,
} from '../components/tariff-assistant-views';
import { VistaHistorial } from '../components/vista-historial';
import { FichaPartida } from '../components/ficha-partida';

/**
 * Asistente Arancelario — sección propia, fuera de Datos Maestros (pedido de
 * Sergio, 2026-08-09): consultar el arancel es una tarea de trabajo diario
 * (clasificar, cotizar una importación), no de configuración.
 *
 * El catálogo es ley nacional y lo mantiene Datec: TODO lo de acá es global
 * (mismas partidas, notas y tasas para todos los clientes); lo único del
 * tenant son sus aperturas y sinónimos.
 *
 * La ficha vive en la URL (?partida=8704.21.10.10) para poder compartirla.
 * Va en el QUERY STRING y no en el pathname a propósito: el router envuelve
 * cada pathname en un <motion.div key={location.pathname}> que REMONTA la
 * página entera al cambiar — con un pathname por ficha, "volver" perdía la
 * búsqueda. El query string no cambia el pathname, así que el buscador queda
 * montado (oculto) mientras la ficha está abierta.
 */
export function TariffAssistantPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get('partida');
  const [vista, setVista] = useState<SubVista>('explorar');

  // El historial es solo para administradores. Se oculta la pestaña, y el
  // backend igual responde 403 — la UI no es el control de acceso, solo evita
  // ofrecer algo que va a fallar.
  //
  // ⚠️ El rol sale del JWT (24 h): si a alguien se le cambia el rol, no ve el
  // cambio hasta cerrar sesión. Y `admin@datec` es COMPANY_ADMIN en dev y
  // PLATFORM_ADMIN en prod, así que los dos tienen que estar contemplados.
  const role = useAuthStore((s) => s.user?.role);
  const esAdmin = role === 'COMPANY_ADMIN' || role === 'PLATFORM_ADMIN';
  const vistasVisibles = useMemo(
    () => SUB_VISTAS.filter((v) => !v.soloAdmin || esAdmin),
    [esAdmin]
  );

  const abrirFicha = (c: string) => setSearchParams({ partida: c });
  const volverABuscar = () => setSearchParams({});

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-8">
        <div className="space-y-6">
          {/* El encabezado de Comercia (artefacto, 19-oct): es el paso «Producto». */}
          <PageHeader
            modulo="comercia"
            etiqueta="Comercia · Producto"
            icono={Sparkles}
            titulo="Asistente arancelario"
            subtitulo="Encontrá la partida de tu producto y mirá qué paga antes de importar. El arancel es ley nacional y lo mantiene Datec: es el mismo para todos los clientes."
          />

          {code && <FichaPartida code={code} onVolver={volverABuscar} />}

          <div className={code ? 'hidden' : 'space-y-5'}>
            {/* Las vistas, como las pestañas del artefacto: el nombre y la raya bajo la elegida. */}
            <div className="flex gap-x-6 overflow-x-auto overflow-y-hidden border-b [scrollbar-width:none] sm:gap-x-8 [&::-webkit-scrollbar]:hidden">
              {vistasVisibles.map((v) => {
                const Icon = v.icon;
                const elegida = vista === v.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    aria-pressed={elegida}
                    onClick={() => setVista(v.id)}
                    className={cn(
                      '-mb-px inline-flex h-12 shrink-0 items-center gap-2 whitespace-nowrap border-b-[3px] px-0.5 text-[15px] transition-colors duration-snap ease-snap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      elegida
                        ? 'border-primary font-semibold text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {v.label}
                  </button>
                );
              })}
            </div>

            {/* Explorar queda siempre montada para conservar la búsqueda al
                volver de la ficha o de otra vista. */}
            <div className={vista === 'explorar' ? '' : 'hidden'}>
              <VistaExplorar onAbrirFicha={abrirFicha} />
            </div>
            {vista === 'aperturas' && <VistaAperturas />}
            {vista === 'sinonimos' && <VistaSinonimos />}
            {vista === 'versiones' && <VistaVersiones />}
            {vista === 'historial' && esAdmin && <VistaHistorial />}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
